import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminSidebar from "./AdminSidebar";
import AdminHeader from "./AdminHeader";
import toast from "react-hot-toast";

export const base_url = process.env.REACT_APP_API_URL;

const SetLimit = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(!(window.innerWidth < 768));
  const [originalLimits, setOriginalLimits] = useState(null);
  const [activeTab, setActiveTab] = useState("cricket");
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const initialLimits = {
    cricket: {
      bookmaker: { min: "", max: "" },
      odds: { min: "", max: "" },
      fancy: { min: "", max: "" },
    },
    soccer: {
      bookmaker: { min: "", max: "" },
      odds: { min: "", max: "" },
      fancy: { min: "", max: "" },
    },
    tennis: {
      bookmaker: { min: "", max: "" },
      odds: { min: "", max: "" },
      fancy: { min: "", max: "" },
    }
  };
  
  const [limits, setLimits] = useState(initialLimits);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Fetch existing limits from the backend
  useEffect(() => {
    const fetchLimits = async () => {
      try {
        setIsLoading(true);
        const auth = localStorage.getItem("admin");
        const res = await axios.get(`${base_url}/api/webapi/admin/getLimit`, {
          headers: {
            Authorization: `${auth}`,
          },
        });

        const fetchedLimits = res.data;
        // console.log("Fetched limits:", fetchedLimits);
        
        // Process the API response format: array of objects with gamename, min, max, game
        const processedLimits = { ...initialLimits };
        
        fetchedLimits.forEach(item => {
          const sport = item.game;
          const gameType = item.gamename;
          
          if (processedLimits[sport] && processedLimits[sport][gameType]) {
            processedLimits[sport][gameType] = {
              min: item.min.toString(),
              max: item.max.toString()
            };
          }
        });
        
        setLimits(processedLimits);
        setOriginalLimits(processedLimits);
      } catch (error) {
        console.error("Failed to fetch limits:", error);
        toast.error("Failed to load limits");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLimits();
  }, []);

  const handleChange = (sport, field, type, value) => {
    setLimits((prev) => ({
      ...prev,
      [sport]: {
        ...prev[sport],
        [field]: {
          ...prev[sport][field],
          [type]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const auth = localStorage.getItem("admin");

      // Prepare payload based on the current active tab data
      // Convert the nested object structure to array format matching the backend
      const currentLimits = limits[activeTab];
      const payload = [];
      
      for (const gameType in currentLimits) {
        payload.push({
          game: activeTab,
          gamename: gameType,
          min: parseInt(currentLimits[gameType].min),
          max: parseInt(currentLimits[gameType].max)
        });
      }

      const res = await axios.post(
        `${base_url}/api/webapi/admin/setLimit`,
        { limits: payload },
        {
          headers: {
            Authorization: `${auth}`,
          },
        }
      );

      toast.success(res.data.message || "Limits updated successfully");
    } catch (err) {
      console.error(err);
      toast.error("Something Went Wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // On desktop, sidebar should always be "open"
      if (!mobile) {
        setIsSidebarOpen(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  if (!originalLimits) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-700 text-lg font-medium">Loading limits...</p>
        </div>
      </div>
    );
  }

  // Map soccer to Football for display purposes
  const displayName = {
    cricket: "Cricket",
    soccer: "Football",
    tennis: "Tennis"
  };

  
  return (
    <div className="wrapper bg-gradient-to-br from-blue-50 to-gray-100 min-h-screen">
      <AdminSidebar 
          isSidebarOpen={isSidebarOpen} 
          toggleSidebar={toggleSidebar} 
          isMobile={isMobile}
        />
        
        {/* Header - Pass mobile info */}
        <AdminHeader 
          toggleSidebar={toggleSidebar} 
          isSidebarOpen={isSidebarOpen}
          isMobile={isMobile}
        />
        
        {/* Content Wrapper - Use conditional class and margin */}
        <div
          className={`content-wrapper ${
            !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
          }`}
          style={{
            marginLeft: (!isMobile && isSidebarOpen) ? "" : "0",
            transition: "margin-left 0.3s ease"
          }}
        >
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="bg-white shadow-lg rounded-2xl p-5 md:p-6 border border-gray-100">
            <h2 className="text-xl md:text-2xl font-bold text-center text-gray-800 mb-1">
              Set Betting Limits
            </h2>
            <p className="text-gray-500 text-center text-sm mb-4">Configure minimum and maximum limits for sports</p>

            {/* Sport selection tabs */}
            <div className="flex mb-6 border-b overflow-x-auto no-scrollbar">
  {Object.keys(limits).map((sport) => (
    <button
      key={sport}
      onClick={() => setActiveTab(sport)}
      className={`px-4 py-2 font-medium text-sm capitalize transition-all whitespace-nowrap relative ${
        activeTab === sport
          ? "text-blue-600 border-b-2 bg-black border-blue-600 -mb-[2px] active-table-color"
          : "text-gray-500 hover:text-gray-700"
      }`}
    >
      {displayName[sport] || sport}
    </button>
  ))}
</div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {Object.keys(limits[activeTab]).map((field) => (
                <div 
                  key={field} 
                  className="bg-gray-50 p-3 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-gray-800 font-medium capitalize text-sm">
                        {field}
                      </label>
                    </div>
                    
                    <div className="flex flex-row space-x-3">
                      <div className="w-1/2">
                        <div className="flex items-center">
                          <div className="bg-gray-200 text-gray-600 text-xs font-medium rounded-l-md px-2 py-3">
                            Min
                          </div>
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">₹</span>
                            <input
                              type="number"
                              placeholder="Min"
                              value={limits[activeTab][field].min}
                              onChange={(e) => handleChange(activeTab, field, "min", e.target.value)}
                              className="border border-gray-300 rounded-r-md pl-6 pr-2 py-3 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="w-1/2">
                        <div className="flex items-center">
                          <div className="bg-gray-200 text-gray-600 text-xs font-medium rounded-l-md px-2 py-3">
                            Max
                          </div>
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">₹</span>
                            <input
                              type="number"
                              placeholder="Max"
                              value={limits[activeTab][field].max}
                              onChange={(e) => handleChange(activeTab, field, "max", e.target.value)}
                              className="border border-gray-300 rounded-r-md pl-6 pr-2 py-3 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex justify-end mt-4">
                <button
                style={{backgroundColor:"black"}}
                  type="submit"
                  disabled={isLoading}
                  className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-medium py-2 px-6 rounded-lg transition duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center text-sm"
                >
                  {isLoading ? (
                    <>
                      <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
                      Updating...
                    </>
                  ) : (
                    `Update ${displayName[activeTab]} Limits`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetLimit;